// Every way to sign in, on one card: a Google or Microsoft account, a passkey, or a code by SMS.
// Registering is the same thing - an account, passkey or number the server has not seen makes a
// new user - so there is no separate sign-up form. Used by the login page and the connect page.
import { useState, type FormEvent } from 'react'
import { auth, errorMessage, type ExternalProvider, type Providers, type SignInResponse } from '../api'
import { passkeyErrorMessage, passkeysSupported, signInWithPasskey } from '../passkeys'
import { ErrorText, Field, Icon, Notice, ProviderIcon, providerName } from './Ui'

interface SignInMethodsProps {
  providers: Providers
  /** Where the browser lands after a Google or Microsoft round trip. */
  returnUrl: string
  onSignedIn: (result: SignInResponse) => void
  /** An error to show at the top, such as the one the external callback redirects here with. */
  initialError?: string | null
}

export function SignInMethods({ providers, returnUrl, onSignedIn, initialError }: SignInMethodsProps) {
  const [error, setError] = useState<string | null>(initialError ?? null)
  const [busy, setBusy] = useState<'passkey' | 'sms' | null>(null)
  const external: ExternalProvider[] = [
    ...(providers.google ? (['google'] as const) : []),
    ...(providers.microsoft ? (['microsoft'] as const) : []),
  ]
  const passkeys = providers.passkey && passkeysSupported()
  const anything = external.length > 0 || passkeys || providers.sms

  async function usePasskey() {
    setBusy('passkey')
    setError(null)
    try {
      onSignedIn(await signInWithPasskey())
    } catch (err) {
      setError(passkeyErrorMessage(err) ?? errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="signin">
      <ErrorText error={error} />
      {!anything && (
        <Notice kind="warn">
          No way to sign in is available right now. Either the server is unreachable, or none of Google, Microsoft, passkeys and SMS has been configured on
          it yet.
        </Notice>
      )}
      {external.length > 0 && (
        <div className="signin-external">
          {external.map((provider) => (
            <a key={provider} className="button button-provider" href={auth.externalUrl(provider, returnUrl)}>
              <ProviderIcon provider={provider} />
              Continue with {providerName(provider)}
            </a>
          ))}
        </div>
      )}
      {passkeys && (
        <button type="button" className="button button-provider" disabled={busy !== null} onClick={usePasskey}>
          <Icon name="key" className="provider-icon" />
          {busy === 'passkey' ? 'Waiting for the passkey…' : 'Continue with a passkey'}
        </button>
      )}
      {(external.length > 0 || passkeys) && providers.sms && <div className="signin-divider">or use your mobile number</div>}
      {providers.sms && (
        <SmsSignIn
          disabled={busy === 'passkey'}
          onBusy={(b) => setBusy(b ? 'sms' : null)}
          onError={setError}
          onSignedIn={onSignedIn}
        />
      )}
      {anything && (
        <p className="signin-footnote">
          New here? Any of these makes your account the first time you use it. There are no passwords to remember: add a passkey afterwards for the
          quickest way back in.
        </p>
      )}
    </div>
  )
}

interface SmsSignInProps {
  disabled?: boolean
  onBusy: (busy: boolean) => void
  onError: (message: string | null) => void
  onSignedIn: (result: SignInResponse) => void
  /** Text for the second button; "Sign in" by default, "Verify" when adding a number to an account. */
  verifyLabel?: string
}

/** The two-step SMS form: a number, then the code that arrived. Also used on the account page to add a number. */
export function SmsSignIn({ disabled, onBusy, onError, onSignedIn, verifyLabel = 'Sign in' }: SmsSignInProps) {
  const [mobile, setMobile] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  function setBusyBoth(value: boolean) {
    setBusy(value)
    onBusy(value)
  }

  async function send(e: FormEvent) {
    e.preventDefault()
    setBusyBoth(true)
    onError(null)
    try {
      const result = await auth.smsSend(mobile)
      setSentTo(result.mobile)
      setCode('')
    } catch (err) {
      onError(errorMessage(err))
    } finally {
      setBusyBoth(false)
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault()
    if (!sentTo) return
    setBusyBoth(true)
    onError(null)
    try {
      onSignedIn(await auth.smsVerify(sentTo, code))
    } catch (err) {
      onError(errorMessage(err))
    } finally {
      setBusyBoth(false)
    }
  }

  if (sentTo) {
    return (
      <form className="signin-sms" onSubmit={verify}>
        <Field label={`Code sent to ${sentTo}`} hint="Six digits. It is good for a few minutes.">
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6 digits"
          />
        </Field>
        <div className="row">
          <button className="button button-primary" disabled={busy || disabled || code.length !== 6}>
            {busy ? 'Checking…' : verifyLabel}
          </button>
          <button type="button" className="button button-quiet" disabled={busy} onClick={() => setSentTo(null)}>
            Other number
          </button>
        </div>
      </form>
    )
  }

  return (
    <form className="signin-sms" onSubmit={send}>
      <Field label="Mobile number" hint="With the country code, such as +47 912 34 567.">
        <input type="tel" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+47 …" />
      </Field>
      <button className="button button-primary" disabled={busy || disabled || mobile.trim().length < 6}>
        <Icon name="phone" size={16} />
        {busy ? 'Sending…' : 'Send me a code'}
      </button>
    </form>
  )
}
